import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  FiHome,
  FiList,
  FiUsers,
  FiMessageSquare,
  FiUser,
  FiPlusCircle,
  FiBell
} from 'react-icons/fi';
import './TopMenu.css';

const TopMenu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(location.pathname);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  
  // Mock des notifications (à remplacer par vos données réelles)
  const [notifications] = useState([
    { id: 1, title: 'Nouvel expert disponible', type: 'expert', time: '2 min' },
    { id: 2, title: 'Réponse sur le forum', type: 'forum', time: '10 min' },
    { id: 3, title: 'Annonce correspondante', type: 'annonce', time: '1 h' }
  ]);
  
  const notificationCount = notifications.length;

  // Vérifier l'authentification au chargement et quand la location change
  useEffect(() => {
    checkAuthentication();
  }, [location]);

  const checkAuthentication = () => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
      // Récupérer les infos utilisateur depuis localStorage
      try {
        const userData = JSON.parse(localStorage.getItem('user') || 'null');
        setUser(userData);
      } catch (error) {
        console.error('Erreur lors de la récupération des données utilisateur:', error);
        setUser(null);
      }
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const menuItems = [
    {
      id: 'home',
      path: '/',
      label: 'Accueil',
      icon: FiHome,
      exact: true
    },
    {
      id: 'annonces',
      path: '/annonces',
      label: 'Annonces',
      icon: FiList,
      exact: false
    },
    {
      id: 'experts',
      path: '/experts',
      label: 'Experts',
      icon: FiUsers,
      exact: false
    },
    {
      id: 'forum',
      path: '/forum',
      label: 'Forum',
      icon: FiMessageSquare,
      exact: false
    }
  ];

  const handleNavigation = (path) => {
    setActiveTab(path);
    navigate(path);
  };

  const handleCreateAction = () => {
    if (isAuthenticated && user) {
      if (user.role === 'entreprise') {
        navigate('/deposer-annonce');
      } else if (user.role === 'expert' || user.role === 'mentor') {
        navigate('/forum/nouvelle-discussion');
      } else {
        navigate('/annonces');
      }
    } else {
      navigate('/connexion');
    }
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const handleNotificationClick = (notification) => {
    switch(notification.type) {
      case 'expert':
        navigate('/experts');
        break;
      case 'forum':
        navigate('/forum');
        break;
      case 'annonce':
        navigate('/annonces');
        break;
      default:
        navigate('/');
    }
    setShowNotifications(false);
  };

  // Fonction pour obtenir l'initiale de l'utilisateur
  const getUserInitial = () => {
    if (!user) return null;
    if (user.prenom) return user.prenom.charAt(0).toUpperCase();
    if (user.name) return user.name.charAt(0).toUpperCase();
    if (user.email) return user.email.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <>
      {/* Overlay pour fermer les notifications */}
      {showNotifications && (
        <div 
          className="topmenu-notifications-overlay"
          onClick={() => setShowNotifications(false)}
        />
      )}

      <div className="topmenu-container">
        {/* Items de navigation */}
        <div className="topmenu-items">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.exact 
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            
            return (
              <button
                key={item.id}
                className={`topmenu-item ${isActive ? 'active' : ''}`}
                onClick={() => handleNavigation(item.path)}
                aria-label={item.label}
              >
                <div className="topmenu-item-content">
                  <Icon className="topmenu-item-icon" />
                  <span className="topmenu-item-label">{item.label}</span>
                </div>
                {isActive && <div className="topmenu-item-indicator" />}
              </button>
            );
          })}
        </div>

        {/* Bouton de création (au centre) */}
        <div className="topmenu-center-action">
          <button 
            className="topmenu-create-btn"
            onClick={handleCreateAction}
            aria-label="Créer"
            title={isAuthenticated ? "Créer un contenu" : "Se connecter"}
          >
            <FiPlusCircle className="topmenu-create-icon" />
          </button>
        </div>

        {/* Actions utilisateur à droite */}
        <div className="topmenu-user-actions">
          {/* Notifications */}
          <div className="topmenu-notification-wrapper">
            <button 
              className="topmenu-notification-btn"
              onClick={toggleNotifications}
              aria-label="Notifications"
              title="Notifications"
            >
              <FiBell className="topmenu-notification-icon" />
              {notificationCount > 0 && (
                <span className="topmenu-notification-badge">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </button>

            {/* Dropdown des notifications */}
            {showNotifications && (
              <div className="topmenu-notifications-dropdown">
                <div className="topmenu-notifications-header">
                  <h4>Notifications</h4>
                  <button 
                    className="topmenu-notifications-close"
                    onClick={() => setShowNotifications(false)}
                    aria-label="Fermer"
                  >
                    ×
                  </button>
                </div>
                
                <div className="topmenu-notifications-list">
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <div 
                        key={notification.id}
                        className="topmenu-notification-item"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="topmenu-notification-content">
                          <div className="topmenu-notification-title">
                            {notification.title}
                          </div>
                          <div className="topmenu-notification-meta">
                            <span className="topmenu-notification-type">
                              {notification.type === 'expert' && '👨‍💼 Expert'}
                              {notification.type === 'forum' && '💬 Forum'}
                              {notification.type === 'annonce' && '📢 Annonce'}
                            </span>
                            <span className="topmenu-notification-time">
                              {notification.time}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="topmenu-notifications-empty">
                      <p>Aucune notification</p>
                    </div>
                  )}
                </div>
                
                <div className="topmenu-notifications-footer">
                  <button 
                    className="topmenu-notifications-mark-read"
                    onClick={() => setShowNotifications(false)}
                  >
                    Tout marquer comme lu
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Profil utilisateur */}
          <button 
            className="topmenu-profile-btn"
            onClick={() => navigate(isAuthenticated ? '/mon-compte' : '/connexion')}
            aria-label="Profil"
            title={isAuthenticated ? "Mon compte" : "Se connecter"}
          >
            {isAuthenticated && getUserInitial() ? (
              <div className="topmenu-user-initial">
                {getUserInitial()}
              </div>
            ) : (
              <FiUser className="topmenu-profile-icon" />
            )}
            {isAuthenticated && (
              <div className="topmenu-profile-indicator" />
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default TopMenu;